// dev_lp — WIX-style sticky header + slide-in drawer menu.
// All menu/auth links are INERT (dead links) for now; infrastructure is ready to wire.
// RTL-aware: hamburger + drawer sit on the inline-start edge; switcher on inline-end.

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  DEV_MENU_ITEMS,
  DEV_AUTH_ITEMS,
  deadLinkHandler,
} from "../menuConfig";

interface Props {
  lang: string;
  onLangChange: (code: string) => void;
}

const DevHeader: React.FC<Props> = ({ lang, onLangChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const navItems = DEV_MENU_ITEMS.filter(
    (i) => !["register", "login"].includes(i.i18nKey),
  );

  const close = () => setOpen(false);

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-white/40 shadow-[0_4px_30px_rgba(0,59,123,0.06)]">
        {/* pl-20: reserve physical-left space for the global AccessibilityWidget
            (fixed left-4 top-4) so it never overlaps the header in RTL or LTR */}
        <div className="max-w-7xl mx-auto pr-4 sm:pr-6 pl-20 py-3.5 flex items-center justify-between gap-3">
          {/* inline-start: hamburger + logo */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label={t("a11y.openMenu")}
              aria-expanded={open}
              className="p-2.5 rounded-xl text-gray-700 hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span
              className="text-2xl font-extrabold tracking-tight bg-gradient-to-l from-blue-700 to-sky-500 bg-clip-text text-transparent select-none"
              dir="ltr"
            >
              CarTax
            </span>
          </div>

          {/* inline-end: language switcher */}
          <LanguageSwitcher lang={lang} onChange={onLangChange} />
        </div>
      </header>

      {/* overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-blue-950/40 backdrop-blur-sm z-50 transition-opacity"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* drawer (slides from inline-start) */}
      <aside
        className={`fixed top-0 ltr:left-0 rtl:right-0 h-full w-[19rem] max-w-[85vw] bg-white shadow-2xl z-[60] transform transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "ltr:-translate-x-full rtl:translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={t("menu.title")}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">{t("menu.title")}</h2>
            <button
              type="button"
              onClick={close}
              aria-label={t("a11y.closeMenu")}
              className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-4 py-5 space-y-1">
            {navItems.map((item) => (
              <a
                key={item.i18nKey}
                href={item.route}
                data-devlp-route={item.route}
                onClick={(e) => {
                  deadLinkHandler(e);
                  close();
                }}
                className="block px-3.5 py-3 rounded-xl text-[15px] text-gray-700 font-medium hover:text-blue-700 hover:bg-blue-50 transition-colors"
              >
                {t(`menu.${item.i18nKey}`)}
              </a>
            ))}
          </nav>

          {/* auth actions (inert) */}
          <div className="px-4 py-5 border-t border-gray-100 space-y-2.5">
            <a
              href={DEV_AUTH_ITEMS[0].route}
              data-devlp-route={DEV_AUTH_ITEMS[0].route}
              onClick={(e) => {
                deadLinkHandler(e);
                close();
              }}
              className="block text-center px-4 py-3 rounded-full text-white font-semibold shadow-lg shadow-blue-900/20 bg-gradient-to-l from-blue-700 to-sky-500 hover:opacity-95 transition-opacity"
            >
              {t("menu.register")}
            </a>
            <a
              href={DEV_AUTH_ITEMS[1].route}
              data-devlp-route={DEV_AUTH_ITEMS[1].route}
              onClick={(e) => {
                deadLinkHandler(e);
                close();
              }}
              className="block text-center px-4 py-3 rounded-full font-semibold text-blue-700 border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              {t("menu.login")}
            </a>
          </div>
        </div>
      </aside>
    </>
  );
};

export default DevHeader;
